import {Location} from 'react-router';

import {ReportName} from 'common/report/constants';
import {queryApiUrl} from 'common/report/queries/utils';
import {QueryReason, QueryTrackingProps} from 'common/report/util/query-tracking/types';
import {apiClient} from 'common/util/api-client';
import {ResponseError} from 'common/util/shared-client';

import {reporter} from '../error-reporting';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from '../types';

// Reduce segmentation limits for insights line charts for certain projects
const REDUCED_LINE_LIMIT_PROJECT_IDS = [`2718323`, `2717081`, `2899503`, `2880915`, `2928584`];

// Reduced segmentation limits for all insights charts for certain projects
const REDUCED_LIMIT_PROJECT_IDS = [`2718323`, `2928584`];

// intended to support insights, funnels, retention, and flows
// this function is currently reused in dashboards to enforce a common querying codepath,
// and may be further consolidated by using QueryManager in dashboards as well
export function executeReportQuery(
  endpoint: string,
  queryParams?: Record<string, string>,
  jsonBody?: any,
  abortSignal?: AbortSignal,
  // dashboards currently only handles errors based on status code
  // however, in reports we may want to use more detailed information provided from the server
  throwOnInvalidStatus = true,
): Promise<Response> {
  jsonBody = jsonBody || {};
  const urlParams = new URLSearchParams(queryParams ?? {});

  // Temporary fix for CrunchyRoll + OneFootball
  // TODO jayantj: remove once merger optimization is shipped
  if (
    REDUCED_LINE_LIMIT_PROJECT_IDS.includes(queryParams?.project_id) &&
    endpoint === `insights` &&
    jsonBody?.bookmark?.displayOptions?.chartType === `line` &&
    jsonBody?.queryLimits
  ) {
    jsonBody.queryLimits.limit = 100;
  } else if (
    REDUCED_LIMIT_PROJECT_IDS.includes(queryParams?.project_id) &&
    endpoint === `insights` &&
    jsonBody?.queryLimits
  ) {
    jsonBody.queryLimits.limit = 300;
  }

  // using apiClient.fetch because apiClient.post() doesn't support query api format
  // eslint-disable-next-line local-rules/deprecated-member-access
  return apiClient.fetch(queryApiUrl(`${endpoint}?${urlParams.toString()}`), {
    headers: {'Content-Type': `application/json; charset=UTF-8`},
    method: `POST`,
    body: JSON.stringify(jsonBody),
    throwOnInvalidStatus,
    abortSignal,
  });
}

export const queryModule = {executeReportQuery};

export abstract class QueryManager<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> {
  abstract reportName: ReportName;
  abstract endpoint: string;
  protected abstract getExtraPostParams(bookmarkParams: BookmarkParamsType, location: Location);
  protected abstract transformBookmarkParams(bookmarkParams: BookmarkParamsType);

  public projectId: number;
  public workspaceId: number;

  private queryController: AbortController = new AbortController();

  constructor({projectId, workspaceId}: {projectId: number; workspaceId?: number}) {
    this.projectId = projectId;
    this.workspaceId = workspaceId;
  }

  protected getEndpoint(_bookmarkParams: BookmarkParamsType): string {
    return this.endpoint;
  }

  // optionally implemented to transform a query error into a report-specific type
  public handleQueryError(err: BaseQueryErrorType): QueryErrorType {
    return err as QueryErrorType;
  }

  // not all reports support csv export, so this is optionally implemented
  public getExtraCsvPostParams(_bookmarkParams: BookmarkParamsType) {
    return {};
  }

  private get queryParams() {
    return {
      ...(this.workspaceId ? {workspace_id: this.workspaceId.toString()} : {}),
      project_id: this.projectId.toString(),
    };
  }

  public async csvExportQuery({
    bookmarkParams,
    useQueryCache = true,
  }): Promise<{
    result: string;
    filename?: string;
  }> {
    const postPayload = {
      bookmark: bookmarkParams,
      use_query_cache: useQueryCache,
      ...this.getExtraCsvPostParams(bookmarkParams),
      format: `csv`,
    };

    // no need to abort pending csv queries
    const result = await queryModule.executeReportQuery(
      this.getEndpoint(bookmarkParams),
      this.queryParams,
      postPayload,
      null,
      true,
    );
    const csvData = await result.text();
    const filename = result.headers.get(`Content-Disposition`)?.match(/filename="([^"]+)"/)?.[1];
    return {
      result: csvData,
      filename,
    };
  }

  public abortPendingQuery() {
    this.queryController.abort();
    this.queryController = new AbortController();
  }

  public async query({
    bookmarkParams,
    queryReason,
    hasUnsavedBookmarkChanges,
    useQuerySampling,
    location,
    bookmarkId,
    useQueryCache = true,
  }: {
    bookmarkParams: BookmarkParamsType;
    queryReason: QueryReason;
    hasUnsavedBookmarkChanges: boolean;
    useQuerySampling: boolean;
    location: Location;
    bookmarkId?: null | number;
    useQueryCache?: boolean;
  }): Promise<QueryResultsType | QueryErrorType> {
    this.abortPendingQuery();

    const trackingProps: QueryTrackingProps = {
      is_main_query_for_report: true,
      report_name: this.reportName,
      bookmark_id: bookmarkId,
      has_unsaved_changes: hasUnsavedBookmarkChanges,
      query_reason: queryReason,
    };

    const postPayload = {
      tracking_props: trackingProps,
      // TODO(CC): apply transformBookmarkParams when Insights Query API
      // is ready for a new show section format
      // bookmark: transformBookmarkParams(bookmarkParams),
      bookmark: bookmarkParams,
      ...this.getExtraPostParams(bookmarkParams, location),
      use_query_cache: useQueryCache,
      use_query_sampling: useQuerySampling,
    };

    try {
      // TODO: remove this experimental query param flag for forcing forecast queries
      const searchParams = new URLSearchParams(window?.location?.search ?? ``);
      if (
        [`1`, `true`].includes(searchParams.get(`force_forecast`)) &&
        this.getEndpoint(bookmarkParams) === `insights` &&
        bookmarkParams[`displayOptions`]?.chartType === `line`
      ) {
        bookmarkParams[`forecastComparison`] = {
          trainingMode: searchParams.get(`training`) || `agile`,
          confidence: parseFloat(searchParams.get(`confidence`) || `0.95`),
        };
        postPayload[`queryLimits`] = {limit: parseFloat(searchParams.get(`query_limit`) || `20`)};
      }

      const response = await queryModule.executeReportQuery(
        this.getEndpoint(bookmarkParams),
        this.queryParams,
        postPayload,
        this.queryController.signal,
        false,
      );

      const results = await response.json();

      // query-api responded with a 4XX - get any detailed information from the response
      if (results.error) {
        const error: BaseQueryErrorType = {
          status: response.status,
          error: results.error,
          data: results.data,
          ...results,
        };

        return this.handleQueryError(error);
      }

      // TODO: run handleResults here when QueryManager is used for query promises passed into ReportEditor
      return results;
    } catch (err) {
      if (err instanceof ResponseError) {
        // apiClient will only throw here if response.status >=500
        return this.handleQueryError({error: err.getMessage(), status: err.response.status});
      } else if (err.name === `AbortError`) {
        return null;
      } else {
        // Something went wrong with the frontend processing the result
        const error = err as Error;
        reporter.error(`Error processing ${this.getEndpoint(bookmarkParams)} query`, err);
        return this.handleQueryError({error: error.stack, status: null});
      }
    }
  }

  /**
   * Optional method to override and transform bookmark params or results when query is returned.
   * Sometimes we need to change bookmark settings based on results and vice-versa
   * @param paramsAndResults - current bookmark params and results returned from query
   * @returns params and results, optionally modified
   */
  handleResults(
    paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>,
  ): ParamsAndResults<BookmarkParamsType, QueryResultsType> {
    return paramsAndResults;
  }
}
